import { IntegrationLogResponse } from './integrationLog.types';

export enum AssociationEntity {
  ACCOUNT = 'accounts',
  CONTACT = 'contacts',
  OPPORTUNITY = 'opportunities',
  QUOTE = 'quotes',
  CONTRACT = 'contracts',
  INVOICE = 'invoices',
  PAYMENT = 'payments',
  CREDIT = 'credits',
  CREDIT_NOTE = 'credit-notes',
  REFUND = 'refunds',
  PRODUCT = 'products',
}

export enum AssociationType {
  ASSOCIATED = 'Associated',
  ARCHIVED = 'Archived',
  DISASSOCIATED = 'Disassociated',
}

export enum ExternalSystem {
  HUBSPOT = 'hubspot',
  SALESFORCE = 'salesforce',
  ATTIO = 'attio',
  QUICKBOOKS = 'quickbooks',
  NETSUITE = 'netsuite',
  XERO = 'xero',
  RILLET = 'rillet',
}

export const crmExternalSystems = new Set([
  ExternalSystem.HUBSPOT,
  ExternalSystem.SALESFORCE,
  ExternalSystem.ATTIO,
]);

export const accountingExternalSystems = new Set([
  ExternalSystem.QUICKBOOKS,
  ExternalSystem.NETSUITE,
  ExternalSystem.XERO,
  ExternalSystem.RILLET,
]);

export interface AssociationResponse {
  externalSystem: ExternalSystem;
  externalSystemIdentifier: string;
  externalObjectType: string;
  externalObjectId: string;
  status: AssociationType;
  externalObjectUrl?: string;
}

/**
 * Response of `GET /connector/entities/{mnEntityName}/{mnEntityId}/syncInfo` —
 * the associations for a record plus its most recent sync logs (capped at 20 by
 * the connector, sorted by `lastUpdatedAt` descending).
 */
export interface EntitySyncInfoResponse {
  associations: AssociationResponse[];
  syncLogs: IntegrationLogResponse[];
}

/**
 * The `syncInfo` endpoint's entity path variable is a connector `MnEntityName`,
 * which is singular PascalCase — not the plural/kebab `AssociationEntity` value
 * used by the legacy per-entity associations routes.
 */
export const ASSOCIATION_ENTITY_TO_MN_ENTITY_NAME: Record<
  AssociationEntity,
  string
> = {
  [AssociationEntity.ACCOUNT]: 'Account',
  [AssociationEntity.CONTACT]: 'Contact',
  [AssociationEntity.OPPORTUNITY]: 'Opportunity',
  [AssociationEntity.QUOTE]: 'Quote',
  [AssociationEntity.CONTRACT]: 'Contract',
  [AssociationEntity.INVOICE]: 'Invoice',
  [AssociationEntity.PAYMENT]: 'Payment',
  [AssociationEntity.CREDIT]: 'Credit',
  [AssociationEntity.CREDIT_NOTE]: 'CreditNote',
  [AssociationEntity.REFUND]: 'Refund',
  [AssociationEntity.PRODUCT]: 'Product',
};

/**
 * Sync logs report the target system capitalised (e.g. `"Xero"`, `"Salesforce"`)
 * while associations use the lowercase {@link ExternalSystem} enum. A connector
 * can report a target system this enum does not carry yet, so callers must
 * handle `undefined`.
 */
export function toExternalSystem(
  targetSystemType: string | null | undefined,
): ExternalSystem | undefined {
  const normalized = targetSystemType?.toLowerCase();
  return Object.values(ExternalSystem).find((system) => system === normalized);
}
